import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SubHeaderBox from "../../components/SubHeaderBox";
import { ShowHide } from "../../components/ShowHideProduct";
import { usePDFShowHide } from "../../../../../hooks/usePDFShowHide";
import { AzilisSwitch } from "../../../../../components/Switch";
import { getFlow42_2LCA, updateFlow42_2LCA } from "../../../../../apis/Product/EndOfLifeFlow42Api";
import "./index.css";

const Flow42_2 = ({ productId }) => {
  const { t } = useTranslation();
  const [show, setShow, isPDFMode] = usePDFShowHide(false, {
    pdfDelay: 5000,
    forceShowInPDF: true,
  });
  const shouldShow = isPDFMode || show;

  const [formData, setFormData] = useState({
    designed_for_disassembly: "No",
    dfd_list_parts: "",
    special_measures_health_environment: "No",
    describe_special_measures: "",
    removal_destructive_only: "No",
    describe_destructive_removal: "",
    disassembly_not_on_site: "No",
    difficult_removal_firmly_attached: "No",
    describe_difficult_removal: "",
    mechanical_effort_required: "No",
    can_be_removed_by_hand: "No",
    describe_disassembly_process: "",
    difficulty_in_disassembling_percent: "",
    chemical_cement_bonding: "No",
    self_adhesive_water_soluble: "No",
    cast_in_place: "No",
    welded_in_place: "No",
    riveted_in_place: "No",
    nailed_in_place: "No",
    screwed_in_place: "No",
    bolted_in_place: "No",
    friction_assembly: "No",
    click_assembly: "No",
  });

  const [hasChanges, setHasChanges] = useState(false);

  const fetchData = useCallback(() => {
    if (productId) {
      getFlow42_2LCA({
        productId,
        setData: (data) => {
          if (data) {
            setFormData({
              designed_for_disassembly: data.designed_for_disassembly || "No",
              dfd_list_parts: data.dfd_list_parts || "",
              special_measures_health_environment: data.special_measures_health_environment || "No",
              describe_special_measures: data.describe_special_measures || "",
              removal_destructive_only: data.removal_destructive_only || "No",
              describe_destructive_removal: data.describe_destructive_removal || "",
              disassembly_not_on_site: data.disassembly_not_on_site || "No",
              difficult_removal_firmly_attached: data.difficult_removal_firmly_attached || "No",
              describe_difficult_removal: data.describe_difficult_removal || "",
              mechanical_effort_required: data.mechanical_effort_required || "No",
              can_be_removed_by_hand: data.can_be_removed_by_hand || "No",
              describe_disassembly_process: data.describe_disassembly_process || "",
              difficulty_in_disassembling_percent: data.difficulty_in_disassembling_percent || "",
              chemical_cement_bonding: data.chemical_cement_bonding || "No",
              self_adhesive_water_soluble: data.self_adhesive_water_soluble || "No",
              cast_in_place: data.cast_in_place || "No",
              welded_in_place: data.welded_in_place || "No",
              riveted_in_place: data.riveted_in_place || "No",
              nailed_in_place: data.nailed_in_place || "No",
              screwed_in_place: data.screwed_in_place || "No",
              bolted_in_place: data.bolted_in_place || "No",
              friction_assembly: data.friction_assembly || "No",
              click_assembly: data.click_assembly || "No",
            });
          }
        },
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSwitchChange = (field, value) => {
    handleInputChange(field, value);
    // Clear dependent fields when switching to "No"
    if (value === "No") {
      switch (field) {
        case "designed_for_disassembly":
          handleInputChange("dfd_list_parts", "");
          break;
        case "special_measures_health_environment":
          handleInputChange("describe_special_measures", "");
          break;
        case "removal_destructive_only":
          handleInputChange("describe_destructive_removal", "");
          break;
        case "difficult_removal_firmly_attached":
          handleInputChange("describe_difficult_removal", "");
          break;
        case "can_be_removed_by_hand":
          handleInputChange("describe_disassembly_process", "");
          break;
        default:
          break;
      }
    }
  };

  const handleSave = () => {
    updateFlow42_2LCA({
      productId,
      ...formData,
      callback: () => {
        setHasChanges(false);
        fetchData();
      },
      use_swal: true,
    });
  };

  const isDesignedForDisassemblyNo = formData.designed_for_disassembly === "No";
  const isSpecialMeasuresNo = formData.special_measures_health_environment === "No";
  const isRemovalDestructiveYes = formData.removal_destructive_only === "Yes";
  const isDifficultRemovalYes = formData.difficult_removal_firmly_attached === "Yes";
  const isCanBeRemovedByHandNo = formData.can_be_removed_by_hand === "No";

  return (
    <div className="flow-42-2-container">
      <SubHeaderBox
        subTitle={t("RESOURCE_POTENTIAL") || "Resource Potential"}
        subDescription={t("RESOURCE_POTENTIAL_DESC") || "Enter information about how your product can be dismantled and what will happen to its components at the end of its life."}
        isNormal={true}
        number={"42.2"}
        showSystemBoundary={false}
      />
      <ShowHide show={show} setShow={setShow} />
      {shouldShow && (
        <div className="flow-42-2-content">
          <div className="flow-42-2-form-section">
            {/* Main Questions */}
            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("DESIGNED_FOR_DISASSEMBLY") || "Designed for disassembly (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.designed_for_disassembly === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.designed_for_disassembly}
                  setWhich={(value) => handleSwitchChange("designed_for_disassembly", value)}
                />
              </div>
            </div>

            {isDesignedForDisassemblyNo && (
              <div className="flow-42-2-field-group">
                <div className="flow-42-2-label">
                  {t("DFD_LIST_PARTS") || "DfD list 8,9 parts"}
                </div>
                <input
                  type="text"
                  className="flow-42-2-input"
                  placeholder={t("USER_TYPES") || "User Types..."}
                  value={formData.dfd_list_parts}
                  onChange={(e) => handleInputChange("dfd_list_parts", e.target.value)}
                />
              </div>
            )}

            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("SPECIAL_MEASURES_HEALTH_ENVIRONMENT") || "Special measures to protect health and the environment during demolition/disassembly (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.special_measures_health_environment === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.special_measures_health_environment}
                  setWhich={(value) => handleSwitchChange("special_measures_health_environment", value)}
                />
              </div>
            </div>

            {isSpecialMeasuresNo && (
              <div className="flow-42-2-field-group">
                <div className="flow-42-2-label">
                  {t("DESCRIBE") || "Describe"}
                </div>
                <input
                  type="text"
                  className="flow-42-2-input"
                  placeholder={t("USER_TYPES") || "User Types..."}
                  value={formData.describe_special_measures}
                  onChange={(e) => handleInputChange("describe_special_measures", e.target.value)}
                />
              </div>
            )}

            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("REMOVAL_DESTRUCTIVE_ONLY") || "Removal can only be performed destructively (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.removal_destructive_only === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.removal_destructive_only}
                  setWhich={(value) => handleSwitchChange("removal_destructive_only", value)}
                />
              </div>
            </div>

            {isRemovalDestructiveYes && (
              <div className="flow-42-2-field-group">
                <div className="flow-42-2-label">
                  {t("DESCRIBE_DESTRUCTIVE_REMOVAL") || "Describe destructive removal"}
                </div>
                <input
                  type="text"
                  className="flow-42-2-input"
                  placeholder={t("USER_TYPES") || "User Types..."}
                  value={formData.describe_destructive_removal}
                  onChange={(e) => handleInputChange("describe_destructive_removal", e.target.value)}
                />
              </div>
            )}

            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("DISASSEMBLY_NOT_ON_SITE") || "Disassembly cannot be performed on-site (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.disassembly_not_on_site === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.disassembly_not_on_site}
                  setWhich={(value) => handleInputChange("disassembly_not_on_site", value)}
                />
              </div>
            </div>

            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("DIFFICULT_REMOVAL_FIRMLY_ATTACHED") || "Difficult removal of firmly attached residues (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.difficult_removal_firmly_attached === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.difficult_removal_firmly_attached}
                  setWhich={(value) => handleSwitchChange("difficult_removal_firmly_attached", value)}
                />
              </div>
            </div>

            {isDifficultRemovalYes && (
              <div className="flow-42-2-field-group">
                <div className="flow-42-2-label">
                  {t("DESCRIBE_DIFFICULT_REMOVAL") || "Describe difficult removal"}
                </div>
                <input
                  type="text"
                  className="flow-42-2-input"
                  placeholder={t("USER_TYPES") || "User Types..."}
                  value={formData.describe_difficult_removal}
                  onChange={(e) => handleInputChange("describe_difficult_removal", e.target.value)}
                />
              </div>
            )}

            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("MECHANICAL_EFFORT_REQUIRED") || "Mechanical effort required for disassembly (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.mechanical_effort_required === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.mechanical_effort_required}
                  setWhich={(value) => handleInputChange("mechanical_effort_required", value)}
                />
              </div>
            </div>

            <div className="flow-42-2-question-group">
              <div className="flow-42-2-label">
                {t("CAN_BE_REMOVED_BY_HAND") || "Can be removed by hand or with simple tools (Y/N)"}
              </div>
              <div className={`flow-42-2-switch-container ${formData.can_be_removed_by_hand === "Yes" ? "yes-selected" : "no-selected"}`}>
                <AzilisSwitch
                  choice1="Yes"
                  choice2="No"
                  styleNo={1}
                  which={formData.can_be_removed_by_hand}
                  setWhich={(value) => handleSwitchChange("can_be_removed_by_hand", value)}
                />
              </div>
            </div>

            {isCanBeRemovedByHandNo && (
              <div className="flow-42-2-field-group">
                <div className="flow-42-2-label">
                  {t("DESCRIBE_DISASSEMBLY_PROCESS") || "Describe the disassembly process"}
                </div>
                <input
                  type="text"
                  className="flow-42-2-input"
                  placeholder={t("USER_TYPES") || "User Types..."}
                  value={formData.describe_disassembly_process}
                  onChange={(e) => handleInputChange("describe_disassembly_process", e.target.value)}
                />
              </div>
            )}

            <div className="flow-42-2-field-group">
              <div className="flow-42-2-label">
                {t("DIFFICULTY_IN_DISASSEMBLING_PERCENT") || "Difficulty in disassembling the product (%)"}
              </div>
              <input
                type="number"
                className="flow-42-2-input"
                placeholder={t("USER_TYPES") || "User Types..."}
                value={formData.difficulty_in_disassembling_percent}
                onChange={(e) => handleInputChange("difficulty_in_disassembling_percent", e.target.value)}
              />
            </div>

            {/* Assembly Methods Section */}
            <div className="flow-42-2-assembly-methods-section">
              <div className="flow-42-2-section-title">
                {t("ASSEMBLY_METHODS") || "Assembly Methods"}
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("CHEMICAL_CEMENT_BONDING") || "Chemical / cement bonding, insoluble (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.chemical_cement_bonding === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.chemical_cement_bonding}
                    setWhich={(value) => handleInputChange("chemical_cement_bonding", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("SELF_ADHESIVE_WATER_SOLUBLE") || "Self-adhesive or water-soluble construction products used (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.self_adhesive_water_soluble === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.self_adhesive_water_soluble}
                    setWhich={(value) => handleInputChange("self_adhesive_water_soluble", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("CAST_IN_PLACE") || "Cast in place (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.cast_in_place === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.cast_in_place}
                    setWhich={(value) => handleInputChange("cast_in_place", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("WELDED_IN_PLACE") || "Welded in place (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.welded_in_place === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.welded_in_place}
                    setWhich={(value) => handleInputChange("welded_in_place", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("RIVETED_IN_PLACE") || "Riveted in place (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.riveted_in_place === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.riveted_in_place}
                    setWhich={(value) => handleInputChange("riveted_in_place", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("NAILED_IN_PLACE") || "Nailed in place (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.nailed_in_place === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.nailed_in_place}
                    setWhich={(value) => handleInputChange("nailed_in_place", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("SCREWED_IN_PLACE") || "Screwed in place (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.screwed_in_place === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.screwed_in_place}
                    setWhich={(value) => handleInputChange("screwed_in_place", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("BOLTED_IN_PLACE") || "Bolted in place (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.bolted_in_place === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.bolted_in_place}
                    setWhich={(value) => handleInputChange("bolted_in_place", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("FRICTION_ASSEMBLY") || "Friction assembly (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.friction_assembly === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.friction_assembly}
                    setWhich={(value) => handleInputChange("friction_assembly", value)}
                  />
                </div>
              </div>

              <div className="flow-42-2-question-group">
                <div className="flow-42-2-label">
                  {t("CLICK_ASSEMBLY") || "Click assembly or similar (Y/N)"}
                </div>
                <div className={`flow-42-2-switch-container ${formData.click_assembly === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.click_assembly}
                    setWhich={(value) => handleInputChange("click_assembly", value)}
                  />
                </div>
              </div>
            </div>

          </div>

          {hasChanges && (
            <div className="flow-42-2-save-container">
              <button className="flow-42-2-save-btn" onClick={handleSave}>
                {t("SAVE") || "Save"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Flow42_2;

